<script setup lang="ts">
import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import {
  clinicalRecordHistoryApi,
  type ClinicalRecordItemType,
  type ClinicalRecordVersion,
} from '@/api/clinical-record-history';
import ResponsiveDialog from '@/components/responsive-dialog.vue';

// The change history of one registro clínico: every write, newest first,
// with when, who and each field before and after.
const props = defineProps<{
  itemType: ClinicalRecordItemType;
  itemId: number;
  // What the registro is, shown under the title ("Registro escrito · 10/03 14:00").
  label: string;
}>();

const open = defineModel<boolean>({ default: false });

const { data: versions, isPending, isError, refetch } = useQuery({
  queryKey: computed(() => ['clinicalRecordHistory', props.itemType, props.itemId]),
  queryFn: () => clinicalRecordHistoryApi.show(props.itemType, props.itemId),
  enabled: open,
});

const EVENTS: Record<ClinicalRecordVersion['event'], { label: string; color: string }> = {
  create: { label: 'Registrado', color: 'primary' },
  update: { label: 'Editado', color: 'grey-darken-3' },
  void: { label: 'Anulado', color: 'error' },
  destroy: { label: 'Eliminado', color: 'error' },
};

const entries = computed(() => versions.value ?? []);
</script>
<template>
  <responsive-dialog
    v-model="open"
    title="Historial de cambios"
    max-width="640"
    scrollable
  >
    <p class="text-subtitle-2 text-medium-emphasis mb-4">
      {{ props.label }}
    </p>
    <div
      v-if="isPending"
      class="d-flex justify-center pa-8"
    >
      <v-progress-circular
        indeterminate
        color="primary"
        aria-label="Cargando el historial"
      />
    </div>
    <v-alert
      v-else-if="isError"
      type="error"
      variant="tonal"
      density="compact"
    >
      No se pudo cargar el historial. Intenta nuevamente.
      <template #append>
        <v-btn
          variant="text"
          size="small"
          @click="refetch()"
        >
          Reintentar
        </v-btn>
      </template>
    </v-alert>
    <p
      v-else-if="entries.length === 0"
      class="text-body-2 text-medium-emphasis text-center pa-6"
    >
      No hay cambios registrados desde que se activó el historial.
    </p>
    <ol
      v-else
      class="history-timeline"
    >
      <li
        v-for="entry in entries"
        :key="entry.id"
        class="history-entry pb-4"
      >
        <div class="d-flex flex-wrap align-center ga-2 mb-2">
          <v-chip
            :color="EVENTS[entry.event].color"
            size="small"
            variant="tonal"
            label
          >
            {{ EVENTS[entry.event].label }}
          </v-chip>
          <span
            v-if="entry.part"
            class="text-body-2 font-weight-medium"
          >
            {{ entry.part }}
          </span>
          <span class="text-body-2 text-medium-emphasis">
            {{ entry.createdAt }} · {{ entry.author }}
          </span>
        </div>
        <dl class="history-changes text-body-2">
          <div
            v-for="change in entry.changes"
            :key="change.field"
            class="mb-2"
          >
            <dt class="font-weight-medium">
              {{ change.label }}
            </dt>
            <dd
              v-if="entry.event === 'update'"
              class="history-values"
            >
              <div>
                <span class="text-medium-emphasis">Antes: </span>
                <span class="pre-wrap">{{ change.oldValue ?? '—' }}</span>
              </div>
              <div>
                <span class="text-medium-emphasis">Después: </span>
                <span class="pre-wrap">{{ change.newValue ?? '—' }}</span>
              </div>
            </dd>
            <dd
              v-else
              class="history-values pre-wrap"
            >
              {{ (entry.event === 'destroy' ? change.oldValue : change.newValue) ?? '—' }}
            </dd>
          </div>
        </dl>
      </li>
    </ol>
  </responsive-dialog>
</template>
<style scoped>
.history-timeline {
  list-style: none;
  padding: 0;
  margin: 0;
}

.history-entry + .history-entry {
  border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  padding-top: 16px;
}

.history-changes {
  margin: 0;
  padding-left: 12px;
  border-left: 3px solid rgb(var(--v-theme-primary));
}

.history-values {
  margin: 0;
}

.pre-wrap {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
</style>
